// Tabuleiro em PixiJS (T-CLIENT-02 e T-CLIENT-04).
// - Planejamento: suas 4 linhas (embaixo) + banco de 9; arrastar e soltar entre
//   casas e banco, soltar sobre a loja para vender, toque simples seleciona.
// - Combate: replay dos eventos do simulador (spawn, move, attack, cast, damage,
//   death), com você sempre embaixo.
import { Application, Assets, Container, Graphics, Sprite, Text, type FederatedPointerEvent, type Texture } from "pixi.js";
import { play } from "../audio/sfx";
import { BOARD_COLS, BOARD_ROWS, CELL_COUNT, mirror } from "@shared/hex";
import type { PlayerState, UnitInstance } from "@shared/match/types";
import type { CombatEvent } from "@shared/combat/simulate";
import type { Star } from "@shared/types";
import { allItemIds, itemIcon, set, speciesFor, unitItemSlots } from "../data";
import { loadPixiSheets, preloadAnims } from "../sprites";
import { hideTip, showTipAt } from "../tooltip";
import { PokemonSprite } from "./pokemon";
import { t, type MsgKey } from "../i18n";
import { ArenaLayer } from "./arena";

export type Slot = { area: "board" | "bench"; index: number };

export interface BoardCallbacks {
  onMove(from: Slot, to: Slot): void;
  onSell(uid: string): void;
  onSelect(uid: string | null): void;
}

interface UnitView {
  root: Container;
  poke: PokemonSprite;
  hpFill: Graphics;
  manaFill: Graphics;
  bars: Container;
  maxHp: number;
  maxMana: number;
  tx: number;
  ty: number;
  uid: string;
  dead: boolean;
}

const MAX_BENCH = 10;
const TICK_MS = 50;
const SQRT3 = Math.sqrt(3);
const FONT = ["Jersey 10", "DotGothic16", "Trebuchet MS", "sans-serif"];

export class BoardRenderer {
  private app = new Application();
  private ready: Promise<void>;
  private arena = new ArenaLayer();
  private grid = new Container();
  private unitsLayer = new Container();
  private fx = new Container();
  private hexW = 40;
  private hexH = 46;
  private ox = 0;
  private oy = 0;
  private benchY = 0;
  private benchSize = 40;
  private benchX = 0;
  private benchSlots = 9;
  private me: PlayerState | null = null;
  private mode: "planning" | "combat" = "planning";
  private views = new Map<string, UnitView>();
  private drag: { uid: string; from: Slot; view: UnitView; sx: number; sy: number; moved: boolean } | null = null;
  private combat: { events: CombatEvent[]; idx: number; t0: number; flip: boolean } | null = null;
  private floaters: { t: Text; life: number }[] = [];
  private selected: string | null = null;
  private resizeObs: ResizeObserver;
  private destroyed = false;

  constructor(private host: HTMLElement, private cb: BoardCallbacks) {
    this.ready = this.init();
    this.resizeObs = new ResizeObserver(() => this.layout());
    this.resizeObs.observe(host);
  }

  private async init() {
    await this.app.init({
      resizeTo: this.host,
      backgroundAlpha: 0,
      antialias: false,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
    });
    if (this.destroyed) return;
    await Promise.all([loadPixiSheets(), document.fonts?.load('16px "Jersey 10"').catch(() => undefined)]);
    // ícones de itens (pequenos) e, em segundo plano, as animações do set
    await Assets.load(allItemIds.map((id) => itemIcon(id))).catch(() => undefined);
    void preloadAnims(set.units.flatMap((u) => u.stages.filter((s): s is number => typeof s === "number")));
    this.host.appendChild(this.app.canvas);
    this.app.stage.addChild(this.arena, this.grid, this.unitsLayer, this.fx);
    this.app.stage.eventMode = "static";
    this.app.stage.hitArea = this.app.screen;
    this.app.stage.on("pointermove", (e) => this.onPointerMove(e));
    this.app.stage.on("pointerup", (e) => this.onPointerUp(e));
    this.app.stage.on("pointerupoutside", (e) => this.onPointerUp(e));
    this.app.ticker.add((t) => this.update(t.deltaMS));
    void this.arena.set(null).then(() => this.drawGrid());
    this.layout();
  }

  destroy() {
    this.destroyed = true;
    this.resizeObs.disconnect();
    void this.ready.then(() => this.app.destroy(true, { children: true }));
  }

  // ------------------------------------------------------------- layout
  private layout() {
    if (!this.app.renderer) return;
    const w = this.host.clientWidth, h = this.host.clientHeight;
    const benchGap = 10;
    // altura: 8 linhas de hex + vão + banco (slot ≈ 0,95 hexW)
    const byW = w / (BOARD_COLS + 0.5);
    const byH = h / (((1 + (BOARD_ROWS - 1) * 0.75) * 2) / SQRT3 + 0.95 + benchGap / Math.max(1, byW));
    this.hexW = Math.max(18, Math.min(byW, byH, 96));
    this.hexH = (this.hexW * 2) / SQRT3;
    const boardW = this.hexW * (BOARD_COLS + 0.5);
    const boardH = this.hexH * (1 + (BOARD_ROWS - 1) * 0.75);
    this.benchSlots = Math.min(MAX_BENCH, this.me?.bench.length ?? 9);
    this.benchSize = Math.min(this.hexW * 0.95, (w - 8) / this.benchSlots);
    const total = boardH + benchGap + this.benchSize;
    this.ox = (w - boardW) / 2;
    this.oy = Math.max(0, (h - total) / 2);
    this.benchY = this.oy + boardH + benchGap;
    this.benchX = (w - this.benchSize * this.benchSlots) / 2;
    this.arena.resize(w, h, { x: this.ox, y: this.oy, w: boardW, h: boardH });
    this.drawGrid();
    this.rebuild();
  }

  private cellCenter(cell: number): { x: number; y: number } {
    const col = cell % BOARD_COLS, row = Math.floor(cell / BOARD_COLS);
    return {
      x: this.ox + col * this.hexW + (row & 1 ? this.hexW / 2 : 0) + this.hexW / 2,
      y: this.oy + row * this.hexH * 0.75 + this.hexH / 2,
    };
  }

  private benchCenter(i: number): { x: number; y: number } {
    return { x: this.benchX + i * this.benchSize + this.benchSize / 2, y: this.benchY + this.benchSize / 2 };
  }

  /** índice local do lado do jogador (0..27) -> célula absoluta (linhas 4..7) */
  private localToCell(i: number): number {
    return (4 + Math.floor(i / BOARD_COLS)) * BOARD_COLS + (i % BOARD_COLS);
  }

  private drawGrid() {
    this.grid.removeChildren();
    const g = new Graphics();
    const r = this.hexH / 2 - 1.5;
    for (let cell = 0; cell < CELL_COUNT; cell++) {
      const { x, y } = this.cellCenter(cell);
      const pts: number[] = [];
      for (let k = 0; k < 6; k++) {
        const a = (Math.PI / 3) * k - Math.PI / 2;
        pts.push(x + r * Math.cos(a), y + r * Math.sin(a));
      }
      const mine = cell >= CELL_COUNT / 2;
      const dim = this.mode === "planning" && !mine;
      // com tabuleiro da loja, as casas ficam translúcidas para mostrar o fundo
      const themed = this.arena.active;
      g.poly(pts)
        .fill({ color: mine ? 0x24425f : 0x4f2a2e, alpha: themed ? (dim ? 0.08 : 0.22) : dim ? 0.35 : 1 })
        .stroke({ width: 2, color: themed ? 0xffffff : mine ? 0x3aa3ff : 0xd6433a, alpha: themed ? (dim ? 0.2 : 0.45) : dim ? 0.25 : 0.7 });
    }
    // banco
    if (this.mode === "planning") {
      for (let i = 0; i < this.benchSlots; i++) {
        const { x, y } = this.benchCenter(i);
        const s = this.benchSize - 4;
        g.roundRect(x - s / 2, y - s / 2, s, s, 6).fill({ color: 0x1f222a, alpha: this.arena.active ? 0.7 : 1 }).stroke({ width: 2, color: 0x4b5262 });
      }
    }
    this.grid.addChild(g);
  }

  // ----------------------------------------------------------- unidades
  /** espécies com shiny equipado: do jogador (embaixo) e do oponente (em cima) */
  private shinyMine = new Set<number>();
  private shinyEnemy = new Set<number>();

  /** Fundo da arena (id de tabuleiro da loja) ou null para o padrão. */
  async setArena(id: string | null) {
    await this.ready;
    if (this.destroyed) return;
    await this.arena.set(id);
    this.drawGrid();
  }

  setShinies(mine: number[], enemy: number[] = []) {
    this.shinyMine = new Set(mine);
    this.shinyEnemy = new Set(enemy);
    void preloadAnims([...mine, ...enemy], true);
  }

  private makeUnit(uid: string, unitId: string, star: Star, branch: string | undefined, items: string[], size: number, species?: number, enemy = false): UnitView {
    const root = new Container();
    const sp = species ?? speciesFor(unitId, star, branch);
    const poke = new PokemonSprite(sp, (enemy ? this.shinyEnemy : this.shinyMine).has(sp), size, enemy);
    root.addChild(poke);

    const bars = new Container();
    const bw = size * 0.7;
    const hpBg = new Graphics().rect(-bw / 2, 0, bw, 5).fill(0x0b0c0f);
    const hpFill = new Graphics().rect(0, 0, bw - 2, 3).fill(0x3fd16b);
    hpFill.position.set(-bw / 2 + 1, 1);
    const manaBg = new Graphics().rect(-bw / 2, 6, bw, 4).fill(0x0b0c0f);
    const manaFill = new Graphics().rect(0, 0, bw - 2, 2).fill(0x4fa8ff);
    manaFill.position.set(-bw / 2 + 1, 7);
    manaFill.scale.x = 0;
    bars.addChild(hpBg, hpFill, manaBg, manaFill);
    bars.position.set(0, -size * 0.62);
    root.addChild(bars);

    const stars = new Text({
      text: "★".repeat(star),
      style: { fontFamily: FONT, fontSize: Math.max(11, size * 0.26), fill: star === 3 ? 0xffc02e : star === 2 ? 0xe8eef6 : 0x9aa3ad, stroke: { color: 0x0b0c0f, width: 3 }, fontWeight: "400" },
    });
    stars.anchor.set(0.5, 0);
    stars.position.set(0, size * 0.18);
    root.addChild(stars);

    // ícones dos itens (2 componentes = 1 item), com tooltip ao passar o mouse
    const slots = unitItemSlots(items);
    const icon = Math.max(10, Math.min(18, size * 0.24));
    slots.forEach((slot, k) => {
      const tex = Assets.get<Texture>(itemIcon(slot.id));
      if (!tex) return;
      const s = new Sprite(tex);
      s.anchor.set(0.5);
      s.width = s.height = icon;
      s.position.set((k - (slots.length - 1) / 2) * (icon + 1), size * 0.18 + Math.max(9, size * 0.22) + icon / 2);
      s.eventMode = "static";
      s.on("pointerover", (e: FederatedPointerEvent) => {
        if (e.pointerType === "mouse") showTipAt(`item:${slot.id}`, (e.nativeEvent as PointerEvent).clientX, (e.nativeEvent as PointerEvent).clientY - 8);
      });
      s.on("pointerout", () => hideTip());
      root.addChild(s);
    });
    return { root, poke, hpFill, manaFill, bars, maxHp: 1, maxMana: 1, tx: 0, ty: 0, uid, dead: false };
  }

  setPlanning(me: PlayerState | null) {
    const benchChanged = (me?.bench.length ?? 9) !== this.benchSlots;
    this.me = me;
    if (benchChanged) this.layout();
    if (this.mode !== "planning") {
      this.mode = "planning";
      this.combat = null;
      this.drawGrid();
    }
    this.rebuild();
  }

  setSelected(uid: string | null) {
    this.selected = uid;
    for (const v of this.views.values()) v.root.alpha = !uid || v.uid === uid ? 1 : 0.85;
    for (const v of this.views.values()) v.poke.setTint(v.uid === uid ? 0xfff2a8 : 0xffffff);
  }

  private rebuild() {
    if (this.mode !== "planning" || !this.app.renderer) return;
    this.unitsLayer.removeChildren();
    this.views.clear();
    const me = this.me;
    if (!me) return;
    const place = (u: UnitInstance, slot: Slot, x: number, y: number, size: number) => {
      const v = this.makeUnit(u.uid, u.unitId, u.star, u.branch, u.items, size);
      v.bars.visible = false;
      v.root.position.set(x, y);
      v.tx = x;
      v.ty = y;
      v.root.eventMode = "static";
      v.root.cursor = "grab";
      v.root.on("pointerdown", (e: FederatedPointerEvent) => {
        this.drag = { uid: u.uid, from: slot, view: v, sx: e.global.x, sy: e.global.y, moved: false };
        this.unitsLayer.addChild(v.root); // traz para frente
      });
      this.unitsLayer.addChild(v.root);
      this.views.set(u.uid, v);
    };
    me.board.forEach((u, i) => {
      if (!u) return;
      const { x, y } = this.cellCenter(this.localToCell(i));
      place(u, { area: "board", index: i }, x, y, this.hexW);
    });
    me.bench.forEach((u, i) => {
      if (!u) return;
      const { x, y } = this.benchCenter(i);
      place(u, { area: "bench", index: i }, x, y + this.benchSize * 0.1, this.benchSize * 0.9);
    });
    this.setSelected(this.selected);
  }

  // ---------------------------------------------------------- arrastar
  private onPointerMove(e: FederatedPointerEvent) {
    const d = this.drag;
    if (!d) return;
    if (!d.moved && Math.hypot(e.global.x - d.sx, e.global.y - d.sy) > 6) d.moved = true;
    if (d.moved) d.view.root.position.set(e.global.x, e.global.y);
  }

  private onPointerUp(e: FederatedPointerEvent) {
    const d = this.drag;
    if (!d) return;
    this.drag = null;
    if (!d.moved) {
      this.cb.onSelect(this.selected === d.uid ? null : d.uid);
      return;
    }
    // soltou fora do canvas, sobre a loja? vende
    const native = e.nativeEvent as PointerEvent;
    const el = document.elementFromPoint(native.clientX, native.clientY);
    if (el?.closest("[data-sell-zone]")) {
      this.cb.onSell(d.uid);
      return;
    }
    const target = this.slotAt(e.global.x, e.global.y);
    if (target && !(target.area === d.from.area && target.index === d.from.index)) this.cb.onMove(d.from, target);
    // volta para o lugar até o servidor confirmar
    d.view.root.position.set(d.view.tx, d.view.ty);
  }

  private slotAt(x: number, y: number): Slot | null {
    // banco
    if (y >= this.benchY - 4 && y <= this.benchY + this.benchSize + 4) {
      const i = Math.floor((x - this.benchX) / this.benchSize);
      if (i >= 0 && i < this.benchSlots) return { area: "bench", index: i };
    }
    // casa mais próxima do seu lado
    let best = -1, bestD = Infinity;
    for (let i = 0; i < 28; i++) {
      const c = this.cellCenter(this.localToCell(i));
      const dd = (c.x - x) ** 2 + (c.y - y) ** 2;
      if (dd < bestD) { bestD = dd; best = i; }
    }
    return best >= 0 && bestD < (this.hexW * 0.75) ** 2 ? { area: "board", index: best } : null;
  }

  // ----------------------------------------------------------- combate
  async playCombat(events: CombatEvent[], side: "a" | "b") {
    await this.ready;
    this.mode = "combat";
    this.drag = null;
    this.drawGrid();
    this.unitsLayer.removeChildren();
    this.views.clear();
    this.combat = { events, idx: 0, t0: performance.now(), flip: side === "b" };
    play("combatStart");
  }

  private combatCell(cell: number): number {
    return this.combat?.flip ? mirror(cell) : cell;
  }

  private applyEvent(ev: CombatEvent) {
    switch (ev.e) {
      case "spawn": {
        const isEnemy = (ev.team === "a") === this.combat!.flip;
        const v = this.makeUnit(ev.u, ev.unitId, ev.star, undefined, ev.items ?? [], this.hexW, ev.species, isEnemy);
        const { x, y } = this.cellCenter(this.combatCell(ev.cell));
        v.root.position.set(x, y);
        v.tx = x;
        v.ty = y;
        v.maxHp = ev.hp;
        v.maxMana = Math.max(1, ev.maxMana);
        v.manaFill.scale.x = ev.mana / v.maxMana;
        v.hpFill.tint = isEnemy ? 0xef4b3c : 0xffffff;
        v.poke.setDir(isEnemy ? 0 : 4); // você embaixo olha para cima
        this.unitsLayer.addChild(v.root);
        this.views.set(ev.u, v);
        break;
      }
      case "move": {
        const v = this.views.get(ev.u);
        if (!v) break;
        const { x, y } = this.cellCenter(this.combatCell(ev.to));
        v.tx = x;
        v.ty = y;
        break;
      }
      case "attack": {
        play("hit");
        const v = this.views.get(ev.u), t = this.views.get(ev.tgt);
        if (!v || !t) break;
        const dx = t.root.x - v.root.x, dy = t.root.y - v.root.y;
        v.poke.face(dx, dy);
        if (v.poke.animated) {
          v.poke.play("attack", () => {
            if (!t.dead) t.poke.play("hurt");
          });
        } else {
          // sem animação: investida curta na direção do alvo
          const len = Math.hypot(dx, dy) || 1;
          v.root.x += (dx / len) * this.hexW * 0.18;
          v.root.y += (dy / len) * this.hexW * 0.18;
        }
        break;
      }
      case "cast": {
        play("cast");
        const v = this.views.get(ev.u);
        if (!v) break;
        v.manaFill.scale.x = 0;
        const t = this.views.get(ev.tgt);
        if (t) v.poke.face(t.root.x - v.root.x, t.root.y - v.root.y);
        v.poke.play("attack");
        v.poke.setTint(0x9fd4ff);
        setTimeout(() => v.poke.setTint(0xffffff), 250);
        break;
      }
      case "damage": {
        const v = this.views.get(ev.u);
        if (!v) break;
        v.hpFill.scale.x = Math.max(0, ev.hp / v.maxHp);
        const color = ev.mult > 1 ? 0xffc02e : ev.mult < 1 ? 0x9aa3ad : ev.kind === "magic" ? 0x9fd4ff : 0xffffff;
        this.floater(`${ev.amount}${ev.crit ? "!" : ""}`, v.root.x, v.root.y - this.hexW * 0.4, color, ev.crit ? 1.3 : 1);
        if (ev.mult > 1) this.floater(t("fx.effective"), v.root.x, v.root.y - this.hexW * 0.75, 0xffc02e, 0.8);
        break;
      }
      case "heal": {
        const v = this.views.get(ev.u);
        if (!v) break;
        v.hpFill.scale.x = Math.max(0, Math.min(1, ev.hp / v.maxHp));
        if (ev.amount >= 10) this.floater(`+${ev.amount}`, v.root.x, v.root.y - this.hexW * 0.4, 0x3fd16b, 0.9);
        break;
      }
      case "shield": {
        const v = this.views.get(ev.u);
        if (v && ev.t > 0) this.floater(t("fx.shield"), v.root.x, v.root.y - this.hexW * 0.6, 0x9fd4ff, 0.8);
        break;
      }
      case "status": {
        const v = this.views.get(ev.u);
        const STATUSES = ["stun", "sleep", "freeze", "confuse", "burn", "poison", "intangible"];
        const color: Record<string, number> = { burn: 0xee8130, poison: 0xa33ea1, freeze: 0x5ec9c4, sleep: 0xc9d3e0 };
        if (v) this.floater(STATUSES.includes(ev.s) ? t(`fx.${ev.s}` as MsgKey) : ev.s, v.root.x, v.root.y - this.hexW * 0.7, color[ev.s] ?? 0xffffff, 0.75);
        break;
      }
      case "death": {
        play("faint");
        const v = this.views.get(ev.u);
        if (v) {
          v.dead = true;
          v.poke.play("hurt");
        }
        break;
      }
      case "end":
        break;
    }
  }

  private floater(text: string, x: number, y: number, color: number, scale: number) {
    const t = new Text({ text, style: { fontFamily: FONT, fontSize: Math.max(13, this.hexW * 0.34) * scale, fill: color, stroke: { color: 0x0b0c0f, width: 3 }, fontWeight: "400" } });
    t.anchor.set(0.5);
    t.position.set(x + (Math.random() - 0.5) * this.hexW * 0.3, y);
    this.fx.addChild(t);
    this.floaters.push({ t, life: 700 });
  }

  // ------------------------------------------------------------ quadro
  private update(dt: number) {
    if (this.combat) {
      const elapsed = performance.now() - this.combat.t0;
      const evs = this.combat.events;
      while (this.combat.idx < evs.length && evs[this.combat.idx]!.t * TICK_MS <= elapsed) {
        this.applyEvent(evs[this.combat.idx]!);
        this.combat.idx++;
      }
      for (const v of this.views.values()) {
        // anda suavemente até o alvo (animação de andar virada para onde vai)
        const dx = v.tx - v.root.x, dy = v.ty - v.root.y;
        const walking = Math.hypot(dx, dy) > this.hexW * 0.08;
        if (walking && !v.dead) {
          v.poke.face(dx, dy);
          v.poke.loop("walk");
        } else v.poke.loop("idle");
        v.root.x += dx * Math.min(1, dt / 140);
        v.root.y += dy * Math.min(1, dt / 140);
        if (v.dead) {
          v.root.alpha = Math.max(0, v.root.alpha - dt / 400);
          if (v.root.alpha === 0) v.root.visible = false;
        }
      }
    }
    this.arena.update(dt);
    for (const v of this.views.values()) v.poke.update(dt);
    for (const f of this.floaters) {
      f.life -= dt;
      f.t.y -= dt * 0.04;
      f.t.alpha = Math.max(0, f.life / 700);
    }
    const dead = this.floaters.filter((f) => f.life <= 0);
    for (const f of dead) f.t.destroy();
    this.floaters = this.floaters.filter((f) => f.life > 0);
  }
}
